import React, {
  memo,
  useState,
  Fragment,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import cn from 'classnames';
import { IconButton } from '@picsio/ui';
import { Edit } from '@picsio/icons';
import {
  func, bool, string, oneOfType, array, oneOf,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import DT from 'duration-time-conversion';
import { editTranscript, editTranscriptSpeaker } from '../../../../store/actions/assets';
import InputEdit from './InputEdit';
import l18n from '../../../../shared/strings';
import DialogRadios from '../../../dialogRadios';
import * as utils from '../../../../shared/utils';
import Tooltip from '../../../Tooltip/Tooltip';
import Logger from '../../../../services/Logger';

function TranscriptItem({
  isActive,
  setVideoPlayerCurrentTime,
  startTime,
  text,
  speaker = null,
  assetId,
  activeRevisionID = null,
  manageTranscriptPermission,
  transcribing = 'skipped',
  isNewSpeaker = false,
  isReaderView = false,
}) {
  const [isEditSpeaker, setEditSpeaker] = useState(false);
  const [isEditText, setEditText] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const itemRef = useRef();
  const isProcessing = ['waiting', 'running'].includes(transcribing);

  useEffect(() => {
    if (isActive) {
      itemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }, [isActive]);

  const saveSpeaker = useCallback(async (value, checkedValue = 'one') => {
    setLoading(true);
    await dispatch(
      editTranscriptSpeaker({
        assetId,
        startTime: checkedValue === 'one' ? startTime : undefined,
        newSpeaker: value,
        oldSpeaker: speaker,
        revisionId: activeRevisionID,
      }),
    );
    setLoading(false);
  }, [dispatch, activeRevisionID, assetId, startTime, speaker]);

  const handleSaveSpeaker = useCallback(async (value) => {
    const notShowDialog = utils.LocalStorage.get('picsio.notShowEditTranscriptSpeaker');

    if (notShowDialog) {
      await saveSpeaker(value);
      return;
    }
    Logger.log('User', 'TranscriptRenameSpeakerModalShown');
    DialogRadios({
      title: l18n.DIALOGS.EDIT_TRANSCRIPT_SPEAKER.TITLE,
      items: [
        {
          label: l18n.DIALOGS.EDIT_TRANSCRIPT_SPEAKER.RADIO_LABEL_ONE,
          value: 'one',
          checked: true,
          dataTestId: 'option-edit-speaker-one',
        },
        {
          label: l18n.DIALOGS.EDIT_TRANSCRIPT_SPEAKER.RADIO_LABEL_ALL,
          value: 'all',
          dataTestId: 'retry-edit-speaker-all',
        },
      ],
      checkbox: {
        value: false,
        label: l18n.DIALOGS.EDIT_TRANSCRIPT_SPEAKER.CHECKBOX_LABEL,
      },
      onOk: async (checkedValue, checkbox) => {
        if (checkbox) {
          utils.LocalStorage.set('picsio.notShowEditTranscriptSpeaker', true);
        }
        Logger.log('User', 'TranscriptRenameSpeakerConfirmed', { action: checkedValue === 'one' ? `partial ${value} rename` : `entire ${value} rename` });
        await saveSpeaker(value, checkedValue);
      },
      onCancel: () => {
        Logger.log('User', 'TranscriptRenameSpeakerModalClosed');
      },
      cancelName: l18n.DIALOGS.btnCancel,
      okName: l18n.DIALOGS.btnConfirm,
    });
  }, [saveSpeaker]);

  const toggleSpeakerEditMode = useCallback(() => {
    Logger.log('User', `TranscriptSpeakerNameEdit${isEditSpeaker ? 'Canceled' : 'Clicked'}`);
    setEditSpeaker((prev) => !prev);
  }, [isEditSpeaker]);

  const handleSaveText = useCallback(
    async (value) => {
      setLoading(true);
      await dispatch(
        editTranscript({
          assetId,
          startTime,
          text: value,
          revisionId: activeRevisionID,
        }),
      );
      setLoading(false);
      Logger.log('User', 'TranscriptEditsSaved');
    },
    [activeRevisionID, assetId, dispatch, startTime],
  );

  const toggleTextEditMode = useCallback(() => {
    Logger.log('User', `TranscriptEditTranscript${isEditText ? 'Canceled' : 'Clicked'}`);
    setEditText((prev) => !prev);
  }, [isEditText]);

  const startTimeSeconds = DT.t2d(startTime) + DT.t2d('00:00.100');

  return (
    <li
      className={cn('TranscriptItem', { isActive, isLoading })}
      ref={itemRef}
      onClick={(e) => {
        if (e.target.closest('#btnEditSubtitle')) return;
        setVideoPlayerCurrentTime(startTimeSeconds);
      }}
      role="presentation"
    >
      <div className="TranscriptItem__content">
        <If condition={isNewSpeaker && speaker && !isReaderView}>
          <div className={cn('TranscriptItem__row', isEditSpeaker && 'TranscriptItem__row--edit-mode')}>
            <span className="TranscriptItem__time">
              {utils.parseTime(startTimeSeconds)}
            </span>
            <Choose>
              <When condition={!isEditSpeaker}>
                <Tooltip content={speaker} placement="top">
                  <span className="TranscriptItem__speaker TranscriptItem__speaker--not-reader-view">
                    {speaker}:{' '}
                  </span>
                </Tooltip>
                <If condition={manageTranscriptPermission && (speaker || (!speaker && !isProcessing))}>
                  <IconButton
                    id="btnEditSubtitle"
                    onClick={toggleSpeakerEditMode}
                    size="lg"
                    componentProps={{
                      'data-testid': 'btnEditSpeaker',
                    }}
                  >
                    <Edit />
                  </IconButton>
                </If>
              </When>
              <Otherwise>
                <InputEdit
                  onSave={handleSaveSpeaker}
                  onCancel={toggleSpeakerEditMode}
                  defaultValue={speaker}
                  name="editSpeaker"
                />
              </Otherwise>
            </Choose>
          </div>
        </If>
        <div className={cn('TranscriptItem__row', isReaderView && 'TranscriptItem__row--text', isEditText && 'TranscriptItem__row--edit-mode')}>
          <If condition={!speaker && !isReaderView}>
            <span className="TranscriptItem__time">
              {utils.parseTime(startTimeSeconds)}
            </span>
          </If>
          <If condition={isNewSpeaker && speaker && isReaderView}>
            <Tooltip content={speaker} placement="top">
              <span className="TranscriptItem__speaker">
                {speaker}:{' '}
              </span>
            </Tooltip>
          </If>
          <Choose>
            <When condition={!isEditText}>
              <Choose>
                <When condition={Array.isArray(text)}>
                  <span className="TranscriptItem__text">
                    {text.map((item, index) => (
                      <Fragment key={index}>{item}</Fragment>
                    ))}
                  </span>
                </When>
                <Otherwise>
                  <span className="TranscriptItem__text">{text}</span>
                </Otherwise>
              </Choose>
              <If condition={manageTranscriptPermission && !isReaderView}>
                <IconButton
                  id="btnEditSubtitle"
                  onClick={toggleTextEditMode}
                  size="lg"
                  componentProps={{
                    'data-testid': 'btnEditSubtitle',
                  }}
                >
                  <Edit />
                </IconButton>
              </If>
            </When>
            <Otherwise>
              <InputEdit
                onSave={handleSaveText}
                onCancel={toggleTextEditMode}
                defaultValue={text}
                name="editTranscript"
              />
            </Otherwise>
          </Choose>
        </div>
      </div>
    </li>
  );
}

TranscriptItem.propTypes = {
  isActive: bool.isRequired,
  startTime: string.isRequired,
  setVideoPlayerCurrentTime: func.isRequired,
  text: oneOfType([string, array]).isRequired,
  assetId: string.isRequired,
  activeRevisionID: string,
  speaker: string,
  isNewSpeaker: bool,
  isReaderView: bool,
  transcribing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
  manageTranscriptPermission: bool.isRequired,
};

export default memo(TranscriptItem);
